import { z } from "zod";

/**
 * Bestandsreichweite & Nachbestellung: wie lange reicht der Bestand beim aktuellen Absatz, wann muss spätestens
 * nachbestellt werden und wie viel. Bewusst einfach und erklärbar (der Kunde soll jede Zahl nachvollziehen können):
 *   Absatz/Tag   = verkaufte Einheiten im Zeitraum ÷ Tage (neue Artikel: ab dem ersten Verkauf, mind. 7 Tage)
 *   Reichweite   = frei verfügbar ÷ Absatz/Tag
 *   Bestellpunkt = Lieferzeit + Sicherheitspuffer (Tage) – fällt die Reichweite inkl. Zulauf darunter: nachbestellen
 *   Vorschlag    = Absatz/Tag × (Lieferzeit + Puffer + Bestellrhythmus) − frei − Zulauf, aufgerundet auf
 *                  Verpackungseinheit, mindestens Mindestbestellmenge
 */

export const replenishmentSettingsSchema = z.object({
  /** Tage von der Bestellung beim Lieferanten bis zur Einlagerung bei Logiful */
  leadDays: z.number().int().min(0).max(365).default(14),
  /** Sicherheitspuffer in Tagen (Schwankungen, Verzögerungen) */
  safetyDays: z.number().int().min(0).max(180).default(7),
  /** für wie viele Tage eine Bestellung reichen soll (Bestellrhythmus) */
  coverDays: z.number().int().min(1).max(365).default(30),
  /** Zeitraum für den Ø-Absatz */
  windowDays: z.union([z.literal(14), z.literal(30), z.literal(60), z.literal(90)]).default(30),
  /** ab dieser Reichweite gilt ein Artikel als Überbestand */
  overstockDays: z.number().int().min(30).max(1000).default(180),
  /** Ladenhüter: so lange ohne Verkauf gilt ein Artikel mit Bestand als „ohne Absatz“ */
  deadDays: z.number().int().min(30).max(730).default(180),
  /** Lagerkosten je m³ und Tag (EUR netto, wie im Logiful-Angebot, z. B. 0,62) – für die Schätzung je Artikel; leer = keine Schätzung */
  storageCostPerM3Day: z.number().min(0).max(1_000).nullable().default(null),
});
export type ReplenishmentSettings = z.infer<typeof replenishmentSettingsSchema>;
export const DEFAULT_REPLENISHMENT: ReplenishmentSettings = replenishmentSettingsSchema.parse({});

/** je Artikel abweichend (leer = Kunden-Standard) */
export const productReplenishmentSchema = z.object({
  leadDays: z.number().int().min(0).max(365).nullable().optional(),
  safetyDays: z.number().int().min(0).max(180).nullable().optional(),
  /** Verpackungseinheit (z. B. 24 je Karton) – Vorschlag wird darauf aufgerundet */
  packSize: z.number().int().min(1).max(100_000).nullable().optional(),
  /** Mindestbestellmenge beim Lieferanten */
  minQty: z.number().int().min(1).max(1_000_000).nullable().optional(),
  supplier: z.string().trim().max(120).nullable().optional(),
  /** Artikelnummer beim Lieferanten (steht auf der Bestellung) */
  supplierSku: z.string().trim().max(80).nullable().optional(),
  /** Einkaufspreis je Stück, netto in EUR (Bestellung, Kapitalbindung) */
  purchasePrice: z.number().min(0).max(1_000_000).nullable().optional(),
  /** nicht nachbestellen (Auslaufartikel) – erscheint nicht in Vorschlägen und Warnungen */
  ignore: z.boolean().optional(),
});
export type ProductReplenishment = z.infer<typeof productReplenishmentSchema>;

export type ReachStatus = "out" | "critical" | "reorder" | "ok" | "overstock" | "no_sales" | "ignored";

export const REACH_STATUS_LABEL: Record<ReachStatus, string> = {
  out: "Ausverkauft",
  critical: "Kritisch",
  reorder: "Nachbestellen",
  ok: "Ausreichend",
  overstock: "Überbestand",
  no_sales: "Kein Absatz",
  ignored: "Nicht nachbestellen",
};

/** mindestens so viele Tage für den Durchschnitt (sonst verzerren einzelne Verkäufe neuer Artikel) */
export const MIN_EFFECTIVE_DAYS = 7;

/** Tage, durch die der Absatz geteilt wird: Zeitraum, bei neuen Artikeln ab dem ersten Verkauf (mind. 7) */
export const effectiveDays = (windowDays: number, daysSinceFirstSale: number | null) =>
  daysSinceFirstSale == null ? windowDays : Math.max(MIN_EFFECTIVE_DAYS, Math.min(windowDays, daysSinceFirstSale));

export interface ReachInput {
  /** frei verfügbar (Lager-verfügbar − im Portal reserviert) */
  free: number;
  /** im Zulauf (angemeldet, noch nicht eingebucht) */
  incoming: number;
  /** Ø-Absatz je Tag */
  daily: number;
  settings: ReplenishmentSettings;
  product?: ProductReplenishment | null;
}

export interface Reach {
  status: ReachStatus;
  /** Reichweite des freien Bestands in Tagen (null = kein Absatz) */
  daysLeft: number | null;
  /** Reichweite inkl. Zulauf */
  daysWithIncoming: number | null;
  /** spätestens bestellen in … Tagen (≤ 0 = jetzt / überfällig) */
  orderInDays: number | null;
  /** Bestellvorschlag in Stück (0 = nichts nötig) */
  suggested: number;
  leadDays: number;
  safetyDays: number;
}

export function computeReach({ free, incoming, daily, settings, product }: ReachInput): Reach {
  const leadDays = product?.leadDays ?? settings.leadDays;
  const safetyDays = product?.safetyDays ?? settings.safetyDays;
  const base = { leadDays, safetyDays };
  if (product?.ignore) return { ...base, status: "ignored", daysLeft: null, daysWithIncoming: null, orderInDays: null, suggested: 0 };
  if (!(daily > 0)) return { ...base, status: "no_sales", daysLeft: null, daysWithIncoming: null, orderInDays: null, suggested: 0 };
  const daysLeft = free / daily;
  const daysWithIncoming = (free + incoming) / daily;
  const reorderPoint = leadDays + safetyDays;
  const orderInDays = Math.floor(daysWithIncoming - reorderPoint);
  // gleiche Reihenfolge wie reachStatusSql (Datenbank filtert/sortiert danach)
  const status: ReachStatus =
    free <= 0 ? "out"
    : daysWithIncoming < leadDays ? "critical"
    : daysWithIncoming < reorderPoint ? "reorder"
    : daysLeft > settings.overstockDays ? "overstock"
    : "ok";
  let suggested = 0;
  if (status === "out" || status === "critical" || status === "reorder") {
    const need = Math.ceil(daily * (reorderPoint + settings.coverDays) - free - incoming);
    if (need > 0) {
      const pack = product?.packSize ?? 1;
      suggested = Math.max(Math.ceil(need / pack) * pack, product?.minQty ?? 0);
    }
  }
  return { ...base, status, daysLeft, daysWithIncoming, orderInDays, suggested };
}
